/**
 * Cleaner Picker — shared widget for selecting an assigned cleaner
 * (``templates/office/components/_cleaner_picker.html``).
 *
 * Single source of truth for the cleaner-selection UI. The same
 * ``<div class="cleaner-picker-component">`` is rendered by 7 call-sites
 * across the office app — quick-create on the Schedule shell, plus the
 * 6 visit drawers (new, mark-done, reschedule, cleaner-absent, detail,
 * quick-create). Every call-site uses ``single_select=True`` and
 * ``compact=True``; the multi-select branch is kept ready for the
 * future multi-cleaner work tracked in ``docs/MULTI_CLEANER_AUDIT.md``.
 *
 * What the widget does:
 *
 *   1. Search-driven cleaner picker — opens a dropdown on click,
 *      auto-loads the first 20 hits, then debounces typing (300 ms)
 *      against ``/office/api/cleaners/search/``. Search keeps full
 *      ASCII for SQL ILIKE; the API tier handles ranking.
 *
 *   2. Live availability — when ``show_availability`` is on AND a date
 *      and time are known (either passed as data attrs at render time,
 *      OR read live from the form via the well-known input IDs
 *      ``id_scheduled_date`` / ``id_scheduled_time`` /
 *      ``id_scheduled_start_datetime`` / ``id_scheduled_duration_minutes``),
 *      each search hit is annotated with an Available / Busy / Absent
 *      badge. Reading the form IDs *live* is what lets the picker react
 *      to the user picking a different date/time without us having to
 *      re-render the partial.
 *
 *   3. ``exclude_occurrence_id`` — passes that occurrence id to the
 *      availability endpoint so the cleaner currently assigned to the
 *      occurrence being rescheduled isn't flagged as busy with itself.
 *
 *   4. Unavailable selection guard — the option is rendered at half
 *      opacity and a click pulses a red ring on the badge instead of
 *      selecting. ``allow_unavailable`` overrides for office-admin
 *      paths that need to assign anyway.
 *
 *   5. Single vs multi select — single select swaps the hidden control
 *      to ``<input type="hidden">`` so the form posts a scalar; multi
 *      select swaps to ``<select multiple>`` so Django's MultipleChoice
 *      receives a list. (Today every call-site is single, but the
 *      branch stays.)
 *
 *   6. Public API — exposed as ``picker.cleanerPicker.{getSelected,
 *      setSelected, refresh}`` and a ``cleanerSelectionChanged``
 *      CustomEvent on selection change. The event payload is
 *      ``{ selectedCleaners: Array<[id, {name, photoUrl, available}]> }``.
 *      The visit drawers (mark-done, reschedule) listen for the event
 *      to re-validate their submit button — that contract is preserved
 *      verbatim by this migration.
 *
 * Wiring contract:
 *
 *   * ``data-auto-init="cleanerPicker"`` on the wrapper triggers
 *     ``autoInitCleanerPicker`` from app.js's ``_runAutoInit`` on
 *     DOMContentLoaded / htmx:afterSwap of #main-content.
 *
 *   * For drawers loaded via ``loadIntoDrawer`` (visit-drawer.js) the
 *     content is injected without firing _runAutoInit — those
 *     call-sites are wired explicitly via
 *     ``wireCleanerPickersInContent(content)`` from the drawer
 *     loader, mirroring the ``wireActionFormsInContent`` /
 *     ``wireBespokeDrawersInContent`` pattern that already lives in
 *     visit-drawer.js.
 *
 *   * Per-element ``_pickerMounted`` flag keeps things idempotent so
 *     a re-init never stacks listeners (HTMX swap or schedule shell
 *     re-init via the bottom nav).
 */

const PICKER_TRANSLATIONS_ID = 'cleaner-picker-i18n';
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;
const API_URL = '/office/api/cleaners/search/';

const FALLBACK_LABELS = {
  noCleanersMatching: 'No cleaners matching',
  selectDateFirst: 'Select date first',
  checking: 'Checking...',
  available: 'Available',
  absent: 'Absent',
  onLeave: 'On leave',
  busy: 'Busy',
  hasAnotherJob: 'Has another job scheduled',
  networkError: 'Network error',
};

function readTranslations() {
  const el = document.getElementById(PICKER_TRANSLATIONS_ID);
  if (!el) return FALLBACK_LABELS;
  try {
    const data = JSON.parse(el.textContent);
    return Object.assign({}, FALLBACK_LABELS, data);
  } catch {
    return FALLBACK_LABELS;
  }
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}

function readPreselected(picker) {
  const id = picker.id;
  const node = id ? document.getElementById(id + '-preselected') : null;
  if (!node) return [];
  try {
    const data = JSON.parse(node.textContent);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Read the live date/time/duration from the surrounding form (best
 * effort). Uses the well-known IDs that every visit form on the office
 * side exposes. Returns ``{date, time, duration}`` strings ready to
 * concatenate into the API URL — date in dd/mm/yyyy, time in HH:MM.
 *
 * The fallbacks are the values rendered into ``data-*`` at template
 * time. The form-level inputs win because the user may have edited
 * them after the picker mounted.
 */
function resolveScheduleFromForm(fallback) {
  let date = fallback.date || '';
  let time = fallback.time || '';
  let duration = fallback.duration || '120';

  const dtInput = document.getElementById('id_scheduled_start_datetime');
  if (dtInput && dtInput.value) {
    const dt = new Date(dtInput.value);
    if (!isNaN(dt.getTime())) {
      const d = String(dt.getDate()).padStart(2, '0');
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const y = dt.getFullYear();
      date = d + '/' + m + '/' + y;
      const hh = String(dt.getHours()).padStart(2, '0');
      const mm = String(dt.getMinutes()).padStart(2, '0');
      time = hh + ':' + mm;
    }
  }

  const dateInput = document.getElementById('id_scheduled_date');
  if (dateInput) {
    const v = (dateInput.value || '').trim();
    if (v) {
      // YYYY-MM-DD (native) → dd/mm/yyyy
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const parts = v.split('-');
        date = parts[2] + '/' + parts[1] + '/' + parts[0];
      } else {
        // Already in dd/mm/yyyy (Flowbite Datepicker) — pass through
        date = v;
      }
    }
  }

  const timeInput = document.getElementById('id_scheduled_time');
  if (timeInput && timeInput.value) time = timeInput.value;

  const durInput = document.getElementById('id_scheduled_duration_minutes');
  if (durInput && durInput.value) duration = durInput.value;

  return { date, time, duration };
}

function buildAvatarHtml(cleaner) {
  const name = cleaner.name || '';
  const isAvailable = cleaner.available !== false;
  if (cleaner.photo_url) {
    return (
      '<img src="' +
      cleaner.photo_url +
      '" alt="' +
      escapeHtml(name) +
      '" class="w-7 h-7 rounded-full object-cover mr-2.5">'
    );
  }
  const bgClass = isAvailable ? 'bg-primary-600' : 'bg-gray-200';
  const textClass = isAvailable ? 'text-white' : 'text-gray-500';
  const initials =
    cleaner.initials || (name ? name.charAt(0).toUpperCase() : '?');
  return (
    '<div class="w-7 h-7 rounded-full flex items-center justify-center mr-2.5 ' +
    bgClass +
    '">' +
    '<span class="text-xs font-medium ' +
    textClass +
    '">' +
    escapeHtml(initials) +
    '</span>' +
    '</div>'
  );
}

function buildAvailabilityBadge(cleaner, hasAvailability, showAvailability, t) {
  if (!hasAvailability || !showAvailability) return { html: '', cls: '' };
  if (cleaner.available === undefined) {
    return {
      html:
        '<span class="availability-badge px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">' +
        escapeHtml(t.selectDateFirst) +
        '</span>',
      cls: '',
    };
  }
  if (cleaner.available) {
    return {
      html:
        '<span class="availability-badge px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">' +
        escapeHtml(t.available) +
        '</span>',
      cls: '',
    };
  }
  if (cleaner.is_absent) {
    return {
      html:
        '<span class="availability-badge px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300" title="' +
        escapeHtml(cleaner.busy_info || t.onLeave) +
        '">' +
        escapeHtml(t.absent) +
        '</span>',
      cls: 'opacity-50',
    };
  }
  return {
    html:
      '<span class="availability-badge px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" title="' +
      escapeHtml(cleaner.busy_info || t.hasAnotherJob) +
      '">' +
      escapeHtml(t.busy) +
      '</span>',
    cls: 'opacity-50',
  };
}

function initialsFromName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function buildChipHtml(data, isCompact) {
  const avatarSize = isCompact ? 'w-4 h-4' : 'w-5 h-5';
  let avatarHtml;
  if (data.photoUrl) {
    avatarHtml =
      '<img src="' +
      data.photoUrl +
      '" alt="" class="' +
      avatarSize +
      ' rounded-full object-cover mr-1">';
  } else {
    // Initials avatar (photo-less) — harmonised with the job form's cleaner
    // chip so identity reads the same everywhere. Dropping back to a generic
    // person glyph here would lose the at-a-glance identity the avatar gives.
    const initials = data.initials || initialsFromName(data.name);
    const textSize = isCompact ? 'text-[8px]' : 'text-[10px]';
    avatarHtml =
      '<span class="' +
      avatarSize +
      ' rounded-full flex items-center justify-center mr-1 bg-primary-600 flex-shrink-0">' +
      '<span class="' +
      textSize +
      ' font-medium text-white">' +
      escapeHtml(initials) +
      '</span></span>';
  }
  return (
    avatarHtml +
    escapeHtml(data.name) +
    '<button type="button" class="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-blue-400 hover:text-blue-600 dark:hover:text-blue-200" data-remove-cleaner>' +
    '<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">' +
    '<path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>' +
    '</svg>' +
    '</button>'
  );
}

/**
 * Mount a cleaner picker on a single ``.cleaner-picker-component``
 * root element. Idempotent — a second call on the same element is a
 * no-op (we set ``_pickerMounted`` after wiring everything up).
 *
 * No return value; the element gains a ``cleanerPicker`` property
 * holding the public API ``{getSelected, setSelected, refresh}``.
 */
export function mountCleanerPicker(picker) {
  if (!picker || picker._pickerMounted) return;
  picker._pickerMounted = true;

  const t = readTranslations();

  const addBtn = picker.querySelector('.add-cleaner-btn');
  const clearAllBtn = picker.querySelector('.clear-all-btn');
  const dropdown = picker.querySelector('.cleaner-dropdown');
  const searchInput = picker.querySelector('.cleaner-search');
  const cleanerList = picker.querySelector('.cleaner-list');
  const chipsContainer = picker.querySelector('.selected-cleaners-chips');
  const hiddenSelect = picker.querySelector('.hidden-select');
  const searchPrompt = picker.querySelector('.cleaner-search-prompt');
  const loadingState = picker.querySelector('.cleaner-loading');
  const emptyState = picker.querySelector('.cleaner-empty-state');

  if (!addBtn || !dropdown || !searchInput || !cleanerList || !hiddenSelect) {
    // Defensive: a partial render missing one of the critical children
    // means the picker can't function. Bail loudly on dev only.
    if (window && window.console) {
      console.warn('[cleaner-picker] missing required children, skipping mount');
    }
    return;
  }

  const showAvailability = picker.dataset.showAvailability !== 'false';
  const allowUnavailable = picker.dataset.allowUnavailable === 'true';
  const singleSelect = picker.dataset.singleSelect === 'true';
  const isCompact = picker.dataset.compact === 'true';
  const fallbackSchedule = {
    date: picker.dataset.scheduledDate || '',
    time: picker.dataset.scheduledTime || '',
    duration: picker.dataset.scheduledDuration || '120',
  };
  const excludeOccurrenceId = picker.dataset.excludeOccurrenceId || '';

  const selectedCleaners = new Map();
  let searchTimeout = null;
  let highlightedIdx = -1;

  // Hydrate from server-rendered preselection (json_script keyed on
  // ``<picker_id>-preselected`` — see _cleaner_picker.html).
  //
  // The partial emits TWO consecutive batches inside the same array:
  // first the full ``selected_cleaners`` objects (with display_name +
  // photo), then a fallback batch of ``effective_ids`` rendered as
  // ``Cleaner #<id>`` for IDs that came in via ``selected_cleaner_ids``
  // without a matching object. We must NOT let the second batch
  // clobber an already-present id from the first — otherwise a real
  // name like "Pavel Novak" gets overwritten with "Cleaner #993" when
  // the same id appears in both lists (which is the common case for
  // the visit detail drawer's quick-fix picker, which receives both).
  // The legacy IIFE guarded with ``if (!selectedCleaners.has(...))``
  // for the second batch only; the same effect here is "first writer
  // wins", which is correct because ``selected_cleaners`` always
  // ships first in the JSON array.
  readPreselected(picker).forEach((c) => {
    const id = String(c.id);
    if (selectedCleaners.has(id)) return;
    selectedCleaners.set(id, {
      name: c.name || ('Cleaner #' + c.id),
      photoUrl: c.photo_url || '',
      initials: c.initials || initialsFromName(c.name || ''),
      available: true,
    });
  });

  function getOptions() {
    return Array.from(cleanerList.querySelectorAll('.cleaner-option'));
  }

  function clearHighlight() {
    getOptions().forEach((el) =>
      el.classList.remove('bg-gray-100', 'dark:bg-gray-600'),
    );
  }

  function setHighlight(idx) {
    const opts = getOptions();
    if (idx < 0 || idx >= opts.length) return;
    clearHighlight();
    highlightedIdx = idx;
    opts[idx].classList.add('bg-gray-100', 'dark:bg-gray-600');
    opts[idx].scrollIntoView({ block: 'nearest' });
  }

  function renderResults(cleaners, hasAvailability) {
    cleanerList.innerHTML = cleaners
      .map((cleaner) => {
        const isSelected = selectedCleaners.has(String(cleaner.id));
        const badge = buildAvailabilityBadge(
          cleaner,
          hasAvailability,
          showAvailability,
          t,
        );
        const availabilityClass = allowUnavailable ? '' : badge.cls;
        const avatarHtml = buildAvatarHtml(cleaner);
        const availableAttr =
          cleaner.available !== undefined ? cleaner.available : 'unknown';
        return (
          '<li class="cleaner-option px-3 py-2 cursor-pointer hover:bg-[#f6f7f7] dark:hover:bg-gray-700 flex items-center justify-between ' +
          availabilityClass +
          ' ' +
          (isSelected ? 'selected-cleaner' : '') +
          '" data-cleaner-id="' +
          cleaner.id +
          '" data-cleaner-name="' +
          escapeHtml(cleaner.name) +
          '" data-cleaner-photo="' +
          (cleaner.photo_url || '') +
          '" data-cleaner-initials="' +
          escapeHtml(cleaner.initials || '') +
          '" data-available="' +
          availableAttr +
          '">' +
          '<div class="flex items-center">' +
          avatarHtml +
          '<span class="text-sm font-medium text-gray-900 dark:text-white">' +
          escapeHtml(cleaner.name) +
          '</span>' +
          '</div>' +
          '<div class="flex items-center gap-2">' +
          badge.html +
          '<svg class="w-4 h-4 text-blue-500 check-icon ' +
          (isSelected ? '' : 'hidden') +
          '" fill="currentColor" viewBox="0 0 20 20">' +
          '<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>' +
          '</svg>' +
          '</div>' +
          '</li>'
        );
      })
      .join('');
  }

  async function searchCleaners(query) {
    const sched = resolveScheduleFromForm(fallbackSchedule);
    let url =
      API_URL + '?q=' + encodeURIComponent(query) + '&limit=' + SEARCH_LIMIT;
    // Availability needs only a DATE. A time refines it (adds the precise
    // "Busy" time-conflict check); without a time the API still flags
    // ABSENCES across the whole day. This lets the quick-create lane click
    // — which often carries a date but no hour — surface a cleaner who is
    // off that day instead of showing no badge at all.
    if (showAvailability && sched.date) {
      url += '&date=' + encodeURIComponent(sched.date);
      if (sched.time) {
        url +=
          '&time=' +
          encodeURIComponent(sched.time) +
          '&duration=' +
          sched.duration;
      }
      if (excludeOccurrenceId) {
        url += '&exclude_occurrence=' + excludeOccurrenceId;
      }
    }

    try {
      const response = await fetch(url);
      const data = await response.json();
      loadingState.classList.add('hidden');
      if (data.results && data.results.length > 0) {
        renderResults(data.results, showAvailability && !!sched.date);
        emptyState.classList.add('hidden');
      } else {
        cleanerList.innerHTML = '';
        emptyState.textContent = t.noCleanersMatching + ' "' + query + '"';
        emptyState.classList.remove('hidden');
      }
    } catch (error) {
      console.error('Cleaner search error:', error);
      loadingState.classList.add('hidden');
      emptyState.textContent = t.networkError;
      emptyState.classList.remove('hidden');
    }
  }

  function dispatchChange() {
    picker.dispatchEvent(
      new CustomEvent('cleanerSelectionChanged', {
        detail: { selectedCleaners: Array.from(selectedCleaners.entries()) },
      }),
    );
  }

  function updateUI() {
    chipsContainer.innerHTML = '';
    const chipClasses = isCompact
      ? 'inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded-full dark:bg-blue-900 dark:text-blue-300'
      : 'inline-flex items-center px-2.5 py-1 text-sm font-medium text-blue-800 bg-blue-100 rounded-full dark:bg-blue-900 dark:text-blue-300';

    selectedCleaners.forEach((data, id) => {
      const chip = document.createElement('span');
      chip.className = chipClasses;
      chip.dataset.cleanerId = id;
      chip.innerHTML = buildChipHtml(data, isCompact);
      chipsContainer.appendChild(chip);
    });

    if (singleSelect) {
      hiddenSelect.value =
        selectedCleaners.size > 0
          ? Array.from(selectedCleaners.keys())[0]
          : '';
    } else {
      hiddenSelect.innerHTML = '';
      selectedCleaners.forEach((data, id) => {
        const opt = document.createElement('option');
        opt.value = id;
        opt.textContent = data.name;
        opt.selected = true;
        hiddenSelect.appendChild(opt);
      });
    }

    if (clearAllBtn) {
      clearAllBtn.classList.toggle('hidden', selectedCleaners.size === 0);
    }
    if (singleSelect) {
      addBtn.classList.toggle('hidden', selectedCleaners.size > 0);
    }
  }

  // ---------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------

  addBtn.addEventListener('click', function (e) {
    e.stopPropagation();
    dropdown.classList.toggle('hidden');
    if (!dropdown.classList.contains('hidden')) {
      searchInput.value = '';
      searchInput.focus();
      searchPrompt.classList.add('hidden');
      loadingState.classList.remove('hidden');
      emptyState.classList.add('hidden');
      cleanerList.innerHTML = '';
      searchCleaners('');
    }
  });

  // Close dropdown on outside click. We use document.body so the
  // listener survives htmx swaps, and gate via `picker.contains(...)`
  // so a click inside the picker (or its dropdown) doesn't close.
  function onOutsideClick(e) {
    if (!document.body.contains(picker)) {
      // Picker was removed (e.g. drawer closed) — detach ourselves.
      document.removeEventListener('click', onOutsideClick);
      return;
    }
    if (!picker.contains(e.target)) {
      dropdown.classList.add('hidden');
    }
  }
  document.addEventListener('click', onOutsideClick);

  searchInput.addEventListener('input', function () {
    const query = this.value.trim();
    clearTimeout(searchTimeout);
    highlightedIdx = -1;
    searchPrompt.classList.add('hidden');
    loadingState.classList.remove('hidden');
    emptyState.classList.add('hidden');
    searchTimeout = setTimeout(
      () => searchCleaners(query),
      query.length === 0 ? 0 : SEARCH_DEBOUNCE_MS,
    );
  });

  searchInput.addEventListener('keydown', function (e) {
    const opts = getOptions();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (opts.length) setHighlight(Math.min(highlightedIdx + 1, opts.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (opts.length) setHighlight(Math.max(highlightedIdx - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIdx >= 0 && opts[highlightedIdx]) {
        opts[highlightedIdx].click();
      }
    } else if (e.key === 'Escape') {
      dropdown.classList.add('hidden');
      highlightedIdx = -1;
    }
  });

  cleanerList.addEventListener('click', function (e) {
    const option = e.target.closest('.cleaner-option');
    if (!option) return;

    const id = option.dataset.cleanerId;
    const name = option.dataset.cleanerName;
    const photoUrl = option.dataset.cleanerPhoto || '';
    const initials = option.dataset.cleanerInitials || '';
    const available = option.dataset.available;

    if (selectedCleaners.has(id)) {
      selectedCleaners.delete(id);
      option.classList.remove('selected-cleaner');
      const check = option.querySelector('.check-icon');
      if (check) check.classList.add('hidden');
    } else {
      if (!allowUnavailable && showAvailability && available === 'false') {
        const badge = option.querySelector('.availability-badge');
        if (badge) {
          badge.classList.add('animate-pulse', 'ring-2', 'ring-red-400');
          setTimeout(
            () =>
              badge.classList.remove(
                'animate-pulse',
                'ring-2',
                'ring-red-400',
              ),
            1000,
          );
        }
        return;
      }

      if (singleSelect && selectedCleaners.size > 0) {
        selectedCleaners.clear();
        cleanerList.querySelectorAll('.cleaner-option').forEach((opt) => {
          opt.classList.remove('selected-cleaner');
          const c = opt.querySelector('.check-icon');
          if (c) c.classList.add('hidden');
        });
      }

      selectedCleaners.set(id, {
        name,
        photoUrl,
        initials,
        available: available === 'true',
      });
      option.classList.add('selected-cleaner');
      const check = option.querySelector('.check-icon');
      if (check) check.classList.remove('hidden');

      if (singleSelect) {
        dropdown.classList.add('hidden');
      }
    }

    updateUI();
    dispatchChange();
  });

  // Chip-remove delegated click (chips are re-rendered on every
  // updateUI; delegating to the container is simpler than re-wiring
  // each render).
  chipsContainer.addEventListener('click', function (e) {
    const removeBtn = e.target.closest('[data-remove-cleaner]');
    if (!removeBtn) return;
    e.stopPropagation();
    const chip = removeBtn.closest('[data-cleaner-id]');
    if (!chip) return;
    const id = chip.dataset.cleanerId;
    selectedCleaners.delete(id);
    updateUI();
    dispatchChange();
  });

  if (clearAllBtn) {
    clearAllBtn.addEventListener('click', function () {
      selectedCleaners.clear();
      cleanerList.querySelectorAll('.cleaner-option').forEach((opt) => {
        opt.classList.remove('selected-cleaner');
        const c = opt.querySelector('.check-icon');
        if (c) c.classList.add('hidden');
      });
      updateUI();
      dispatchChange();
    });
  }

  // ---------------------------------------------------------------
  // Public API — preserved verbatim from the legacy inline IIFE.
  // External consumers (visit drawers' bespoke modules) read these.
  // ---------------------------------------------------------------
  picker.cleanerPicker = {
    getSelected: () => Array.from(selectedCleaners.entries()),
    setSelected: (cleaners) => {
      selectedCleaners.clear();
      (cleaners || []).forEach((c) => {
        if (c && typeof c === 'object') {
          selectedCleaners.set(String(c.id), {
            name: c.name,
            photoUrl: c.photo_url || c.photoUrl || '',
            initials: c.initials || initialsFromName(c.name || ''),
            available: true,
          });
        } else if (c != null) {
          selectedCleaners.set(String(c), {
            name: 'Cleaner #' + c,
            photoUrl: '',
            available: true,
          });
        }
      });
      updateUI();
    },
    refresh: () => {
      const query = searchInput.value.trim();
      if (query) searchCleaners(query);
    },
  };

  updateUI();
}

/**
 * Walk a freshly injected DOM subtree and mount every cleaner picker
 * inside it. Called by drawer loaders (visit-drawer.js etc.) right
 * after the partial is dropped into the drawer body.
 */
export function wireCleanerPickersInContent(content) {
  if (!content || !content.querySelectorAll) return;
  content.querySelectorAll('.cleaner-picker-component').forEach(mountCleanerPicker);
}

/**
 * Auto-init entry point — wired via ``data-auto-init="cleanerPicker"``
 * on the wrapper div. Used by full-page renders (the Schedule shell)
 * where ``_runAutoInit`` runs naturally on DOMContentLoaded /
 * htmx:afterSwap of #main-content. Safe to re-fire.
 */
export function autoInitCleanerPicker() {
  document
    .querySelectorAll('.cleaner-picker-component[data-auto-init="cleanerPicker"]')
    .forEach(mountCleanerPicker);
}

// Expose on window so legacy non-ESM consumers (visit-drawer.js,
// schedule-quick-create.js — both currently using global wires for
// uniform call-site style) can wire freshly-injected partial content
// without an import statement.
if (typeof window !== 'undefined') {
  window.wireCleanerPickersInContent = wireCleanerPickersInContent;
  window.mountCleanerPicker = mountCleanerPicker;
}
